import { forwardRef, ReactNode, useId, useRef } from "react";
import { SvgIcon } from "@mui/material";
import { CoreIconButton, CoreIconButtonProps } from "./Button";
import CoreMenu, { CoreMenuProps } from "../menu";
import Action from "./images/action.svg?react";
import i18nPackage from "../../i18n/i18n";

export type CoreMenuButtonProps = Omit<
	CoreIconButtonProps,
	"variant" | "aria-expanded" | "aria-controls" | "onClick" | "slotProps"
> & {
	menuShowing?: boolean;
	setMenuShowing: (showing: boolean) => void;
	icon?: ReactNode;
	slotProps?: CoreIconButtonProps["slotProps"] & {
		menu?: Omit<CoreMenuProps, "id" | "open" | "onClose" | "anchorEl" | "children">;
	};
};

const CoreMenuButton = forwardRef<HTMLButtonElement, CoreMenuButtonProps>(function CoreMenuButton(
	{
		id,
		color = "#535353",
		menuShowing = false,
		setMenuShowing,
		slotProps,
		children,
		icon,
		"aria-haspopup": ariaHasPopup = "menu",
		...buttonProps
	},
	ref,
) {
	const buttonRef = useRef<HTMLButtonElement | undefined>(undefined);
	const generatedButtonId = useId();
	const buttonId = id ?? generatedButtonId;
	const menuId = useId();

	return (
		<>
			<CoreIconButton
				{...buttonProps}
				slotProps={slotProps}
				id={buttonId}
				variant="plain"
				color={color}
				onClick={(e) => {
					e.stopPropagation();
					setMenuShowing(true);
				}}
				aria-haspopup={ariaHasPopup}
				aria-expanded={menuShowing}
				aria-controls={menuShowing ? menuId : undefined}
				aria-label={buttonProps["aria-label"] ?? i18nPackage.t("coreComponents.menuButton.ariaLabel")}
				ref={(elem) => {
					if (elem) {
						buttonRef.current = elem;
						if (typeof ref === "function") {
							ref(elem);
						} else if (ref) {
							ref.current = elem;
						}
					}
				}}
			>
				{icon ?? (
					<SvgIcon>
						<Action aria-hidden="true" />
					</SvgIcon>
				)}
			</CoreIconButton>
			{menuShowing && (
				<CoreMenu
					{...slotProps?.menu}
					open
					onClose={() => setMenuShowing(false)}
					anchorEl={buttonRef.current}
					aria-labelledby={buttonId}
					slotProps={{
						...slotProps?.menu?.slotProps,
						menuList: {
							...slotProps?.menu?.slotProps?.menuList,
							id: menuId,
						},
					}}
				>
					{children}
				</CoreMenu>
			)}
		</>
	);
});
export default CoreMenuButton;
